import React, {
  useState,
  useEffect,
  useContext,
  memo,
  useCallback,
  useMemo,
} from "react";
import {
  Button,
  IconButton,
  Stack,
  Box,
  Icon,
  Tooltip,
  Typography,
  Link,
  CircularProgress,
} from "@mui/material";
import { DevicesOutlined, RefreshOutlined } from "@mui/icons-material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import NewGroupDialog from "./dialogs/NewGroupDialog.js";
import AddToGroupDialog from "./dialogs/AddToGroupDialog.js";
import { useRecommendedCalendars } from "src/utils/useRecommendedCalendars.js";
import {
  getPageRoute,
  useGetUserCalendars,
  useUnsubscribeCalendar,
  useRemoveFromGroup,
  useUnsubscribeGroup,
  useValidateSubscription,
  willSubscribeWithExperience,
  hasAccess,
  getUpgradeFlow,
} from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";
import {
  useCheckSubscriptionStatus,
  copyCalendarLinkToClipboard,
  handleUnsubscribe,
  handleSubscribeExperience,
  handleSignInDialog,
  isAddComplete,
  openCalendarWebcal,
  handleResubscribe,
} from "src/utils/Utils.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import SignInDialog from "src/components/blocks/dialogs/SignInDialog.js";
import AddToCalendarDialog from "./dialogs/AddToCalendarDialog.js";
import OpenCalendarDialog from "./dialogs/OpenCalendarDialog.js";
import { useFeatureAccess } from "src/utils/FeatureAccessContext.js";
import { Features } from "src/services/FeatureGatingService.js";
import ShareDialog from "src/components/blocks/dialogs/ShareDialog.js";
import { useTheme } from "@emotion/react";
import { useGroupDialogs } from "src/utils/useGroupDialogs.js";
import { LoadingButton } from "./LoadingButton.js";
import { useMutation } from "@tanstack/react-query";
import { useForceRefreshGoogleCalendar } from "src/services/mutations/useForceRefreshGoogleCalendar.js";
import { useGoogleCalendarStatus } from "src/services/queries/useGoogleCalendarStatus.js";
import { customEvent } from "src/utils/gtag.js";
import {
  GOOGLE_CONNECT_STATES,
  googleConnectState,
} from "src/utils/googleConnect.js";
import { requestChannelDecision } from "src/utils/googleConnectIntercept.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import { rollbar } from "src/utils/ErrorBoundary/index.js";
import FloatingButton from "./FloatingButton.js";
function SubscribeButton({
  calendar,
  onSubscribeAction,
  isEventCard,
  setSelectedCalendar,
  eventId,
  handle: routeHandle,
  ...props
}) {
  const { mutateAsync: validateSubscription } = useValidateSubscription();
  const { mutateAsync: unsubscribeCalendar } = useUnsubscribeCalendar();
  const { mutateAsync: removeFromGroup } = useRemoveFromGroup();
  const { mutateAsync: unsubscribeGroup } = useUnsubscribeGroup();
  const { subscribe } = useSubscribe();
  const [createdGroups, setCreatedGroups] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [openAddToCalendarOrGroupDialog, setOpenAddToCalendarOrGroupDialog] =
    useState(false);
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const [openTimelineDialog, setOpenTimelineDialog] = useState(false);
  const { user, refetchAuth } = useContext(AuthContext);
  const gcState = googleConnectState(user);
  const { requestFeatureAccess } = useFeatureAccess();
  const { refetch: refetchGoogleCalendarStatus } = useGoogleCalendarStatus(
    { user },
    { enabled: false }
  );
  const {
    handleForceRefreshGoogleCalendar,
    isLoading: isLoadingForceRefreshGoogleCalendar,
  } = useForceRefreshGoogleCalendar({ refetchGoogleCalendarStatus });
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const typeGroup = calendar?.calendars;
  const theme = useTheme();

  const { refetchRecommendedCalendars } = useRecommendedCalendars({
    sourceCalendar: calendar,
    enabled: false,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { inGroup, subscribed, setSubscribed, isSubscribed } =
    useCheckSubscriptionStatus({
      user,
      calendar,
    });
  const { data: userCalendars, refetch: refetchUserCalendars } =
    useGetUserCalendars({
      user,
    });
  const {
    openAddToGroup,
    openNewGroup,
    setOpenAddToGroup,
    setOpenNewGroup,
    handleGroupDialog,
    handleAddToGroup,
    handleCreateGroup,
    handleMessage,
    targetCalendar,
    busyGroupId,
  } = useGroupDialogs({
    setCreatedGroups,
    onSubscribeAction,
    subscribed,
  });

  useEffect(() => {
    if (subscribed) {
      setOpenTimelineDialog(false);
    }
    const handleShowTimelineDialog = () => {
      const isTimelinePage = window.location.pathname.includes(
        `${calendar?.handle}`
      );
      if (isTimelinePage && user && !isEventCard) {
        if (
          window.scrollY > window.innerHeight * 0.3 &&
          !openTimelineDialog &&
          !subscribed
        ) {
          setOpenTimelineDialog(true);
        } else if (
          window.scrollY < window.innerHeight * 0.3 &&
          openTimelineDialog &&
          !subscribed
        ) {
          setOpenTimelineDialog(false);
        }
      }
    };
    window.addEventListener("scroll", handleShowTimelineDialog);
    return () => {
      window.removeEventListener("scroll", handleShowTimelineDialog);
    };
  }, [calendar?.handle, isEventCard, openTimelineDialog, subscribed, user]);

  // A blocked channel is the only thing there is to fix from here; a working one,
  // Google Connect or link, has nothing to refresh, so the action is adding the
  // calendar somewhere else rather than minting a new URL.
  const isChannelBlocked =
    gcState === GOOGLE_CONNECT_STATES.BROKEN ||
    gcState === GOOGLE_CONNECT_STATES.INACTIVE;
  const channelActionLabel =
    gcState === GOOGLE_CONNECT_STATES.BROKEN
      ? "Reconnect"
      : gcState === GOOGLE_CONNECT_STATES.INACTIVE
        ? "Resubscribe"
        : "Add to another device";

  const openWebcalForCalendar = () =>
    handleResubscribe({
      user,
      calendar,
      navigate,
      inGroup,
      calendarGroup,
      setSelectedCalendar,
      onMissingWebcalUrl: () => notifyMissingWebcalUrl(user),
      onGoogleProvider: () => setOpenCalendarDialog(true),
      onHasValidGoogleConnection: () => setOpenCalendarDialog(true),
    });

  const handleChannelAction = () => {
    if (isChannelBlocked) {
      requestChannelDecision({
        state: gcState,
        calendar: inGroup && calendarGroup ? calendarGroup : calendar,
        onAddByLink: openWebcalForCalendar,
      });
      return;
    }
    openWebcalForCalendar();
  };

  const copyToClipboard = useCallback(() => {
    copyCalendarLinkToClipboard({
      inGroup,
      user,
      calendar,
      showSnackBar,
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
    });
  }, [
    calendar,
    inGroup,
    setSnackbarMessage,
    setSnackbarOpen,
    setSnackbarSeverity,
    showSnackBar,
    user,
  ]);

  useEffect(() => {
    const subscribing = searchParams.get("subscribing");
    if (subscribing && user) {
      setSnackbarOpen(false);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete("subscribing");
      setSearchParams(newSearchParams);
      try {
        const subscribingObj = JSON.parse(subscribing);
        if (!subscribingObj?.calendarId || !subscribingObj?.handle) {
          throw new Error("Invalid subscribing object");
        }

        const matchCalendar = calendar._id === subscribingObj.calendarId;
        if (matchCalendar) {
          if (!isSubscribed()) {
            handleOpenAddToCalendarOrGroupDialog();
          } else {
            showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              "You've already added this calendar.",
              "success"
            );
          }
        }
      } catch (error) {
        console.error("Error parsing subscribing parameter:", error);
      }
    }
  }, [calendar._id, searchParams, user]);

  const handleShowRelatedCalendars = () => {
    setTimeout(() => {
      const firstRelatedCalendarsButton = document.querySelector(
        "[data-testid='calendar-event-discover-0']"
      );
      if (firstRelatedCalendarsButton) {
        firstRelatedCalendarsButton.click();
        const rect = firstRelatedCalendarsButton.getBoundingClientRect();
        const offset = -50;
        window.scrollTo({
          top: rect.top + window.scrollY + offset,
          behavior: "smooth",
        });
      }
    }, 2000);
  };

  /**
   * Reports a calendar with no webcal URL and points the subscribed user to the Library.
   *
   * @param {object} subscriber - The user the subscribe or resubscribe ran for.
   */
  const notifyMissingWebcalUrl = useCallback(
    (subscriber) => {
      if (rollbar && rollbar.configure) {
        rollbar.error("Missing webcal URL after subscribe", {
          context: "SUBSCRIBE_MISSING_WEBCAL_URL",
          calendarId: calendar?._id,
          calendarHandle: calendar?.handle,
          userId: subscriber?._id,
        });
      }
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "You're subscribed. Open it from your Library to add it to your calendar app.",
        "info"
      );
    },
    [calendar, setSnackbarMessage, setSnackbarOpen, setSnackbarSeverity, showSnackBar]
  );

  const handleSubscribe = useCallback(
    async (targetCalendar, extra = { user }) => {
      try {
        const { user } = extra;
        const subscribesHere = !willSubscribeWithExperience(user);
        if (subscribesHere) {
          const isTimelinePage = location.pathname.includes(
            getPageRoute("calendar", "ALL_EVENTS", { handle: calendar.handle })
          );
          if (isTimelinePage) {
            refetchRecommendedCalendars();
          }
          await subscribe({ calendar: targetCalendar });
          onSubscribeAction?.(true, "calendar");
          showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            "You're all set. It may take a few minutes for events to appear in your calendar app.",
            "success"
          );
          if (isTimelinePage) {
            handleShowRelatedCalendars();
          }
        } else {
          // Free users: validate against the BE so the Stanza+/limit gate
          // fires. A 403 falls into the catch block below which routes to
          // the Trigger page based on upgradeType.
          await validateSubscription({
            calendarId: targetCalendar._id,
          });
        }
        setOpenAddToCalendarOrGroupDialog(false);
        const defaultFn = () => {
          if (user?.provider === "google") {
            setOpenCalendarDialog(true);
          } else if (!openCalendarWebcal({ user, calendar })) {
            // The subscribe already succeeded; only the calendar-app handoff is missing.
            notifyMissingWebcalUrl(user);
          } else {
            showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              <>
                You should have been prompted to open your calendar and
                subscribe. Having issues?{" "}
                <Link
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    window.open(
                      "https://stanza-1.gitbook.io/stanza-help-center/for-subscribers/editor",
                      "_blank"
                    )
                  }
                >
                  Troubleshoot
                </Link>
              </>,
              "success"
            );
          }
        };
        const intercepted = handleSubscribeExperience({
          user,
          navigate,
          calendar,
          defaultFn,
          onChannelIntercept: requestChannelDecision,
        });
        if (isEventCard) {
          if (isAddComplete({ subscribedHere: subscribesHere, intercepted })) {
            setSubscribed(true);
          }
        } else {
          setSelectedCalendar?.(targetCalendar);
        }
        refetchAuth();
      } catch (error) {
        if (error.response && error.response.status === 403) {
          const upgradeType = error.response.data.upgradeType;
          const featureKey =
            upgradeType === "CALENDAR_LIMIT"
              ? "ADD_CALENDAR"
              : "ADD_EXCLUSIVE_CALENDAR";
          // The gated request is for `targetCalendar`; the timeline calendar
          // can be absent or stale, so fall back to the route/path handle.
          const gatedCalendar = targetCalendar || calendar;
          const pathHandle = location.pathname.split("/").filter(Boolean)[0];
          const upgradeCalendar =
            gatedCalendar?.handle || !(routeHandle || pathHandle)
              ? gatedCalendar
              : {
                  ...gatedCalendar,
                  handle: routeHandle || pathHandle,
                };
          const flow = getUpgradeFlow(featureKey, {
            calendar: upgradeCalendar,
          });
          return navigate(flow.route, { state: flow.state });
        }

        // Log additional context for subscription errors not handled by axios interceptor
        if (rollbar && rollbar.configure) {
          try {
            rollbar.error(error, {
              context: "SUBSCRIPTION_HANDLE_ERROR",
              calendarId: targetCalendar?._id,
              calendarHandle: targetCalendar?.handle || calendar?.handle,
              userId: user?._id,
              userEmail: user?.email,
              hasValidSubscription: user?.hasValidSubscription,
              willSubscribeWithExperience: willSubscribeWithExperience(user),
              location: location.pathname,
            });
          } catch (rollbarError) {
            console.error(
              "Failed to report subscription error to Rollbar:",
              rollbarError
            );
          }
        }

        console.error(error);
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          <>
            There was an issue subscribing to this calendar. Please{" "}
            <Link
              as="span"
              sx={{ cursor: "pointer" }}
              onClick={async () => {
                if (hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key)) {
                  await handleForceRefreshGoogleCalendar(calendar);
                } else {
                  openCalendarWebcal({ user, calendar });
                }
                handleSubscribe(targetCalendar);
              }}
            >
              try again
            </Link>{" "}
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                fontWeight: "300",
                fontSize: "1rem",
                verticalAlign: "text-bottom",
              }}
            >
              north_east
            </Icon>
          </>,
          "error"
        );
      }
    },
    [
      calendar,
      handleForceRefreshGoogleCalendar,
      isEventCard,
      location.pathname,
      navigate,
      notifyMissingWebcalUrl,
      onSubscribeAction,
      refetchAuth,
      routeHandle,
      setSelectedCalendar,
      setSnackbarMessage,
      setSnackbarOpen,
      setSnackbarSeverity,
      setSubscribed,
      showSnackBar,
      subscribe,
      user,
      validateSubscription,
    ]
  );

  const handleOpenAddToCalendarOrGroupDialog = useCallback(
    (extra = { user }) => {
      const { user } = extra;
      if (hasAccess(user, Features.ADD_TO_GROUP.key)) {
        // For users with access to groups, show the dialog with options
        setOpenAddToCalendarOrGroupDialog(true);
      } else {
        // If the user doesn't have access to groups, go straight to subscribe
        // This will trigger the backend check and redirect to upgrade if needed
        handleSubscribe(calendar, { user });
      }
    },
    [calendar, handleSubscribe, user]
  );

  const openAddToCalendarFn = useCallback(async () => {
    // Track click first
    customEvent({
      name: "user_click_add_to_calendar",
      category: "SubscribeButton",
      label: "Add to Calendar Button Click",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_id: calendar?._id,
        calendar_handle: calendar?.handle,
        is_event_card: isEventCard,
        in_group: inGroup,
        event_id: eventId,
      },
    });

    const extraSearchParams = {
      subscribing: JSON.stringify({
        calendarId: calendar._id,
        handle: calendar?.handle,
        eventId,
      }),
    };
    if (
      handleSignInDialog(
        user,
        setOpenSignInDialog,
        setSearchParams,
        extraSearchParams
      )
    )
      return;
    // At this point, the user is signed in

    const postPaymentAction = async ({ user }) => {
      // If it's a group type, handle subscription directly
      if (typeGroup) {
        return handleSubscribe(calendar, { user });
      }

      // For non-group types or when typeGroup is not set.
      handleOpenAddToCalendarOrGroupDialog({ user });
    };

    const result = await requestFeatureAccess(Features.ADD_CALENDAR.key, {
      calendar,
      currentPath: window.location.pathname + window.location.search,
      action: postPaymentAction,
      continueText: "Continue Subscribing",
    });

    // If user already has access, execute immediately
    if (result.granted && result.reason === "has_access") {
      await postPaymentAction({ user });
    }
  }, [
    calendar,
    eventId,
    handleOpenAddToCalendarOrGroupDialog,
    handleSubscribe,
    inGroup,
    isEventCard,
    requestFeatureAccess,
    setSearchParams,
    typeGroup,
    user,
  ]);

  const {
    mutateAsync: handleOpenAddToCalendar,
    isLoading: isLoadingOpenAddToCalendar,
  } = useMutation(openAddToCalendarFn);

  const calendarGroup = useMemo(() => {
    if (userCalendars?.createdGroups) {
      return userCalendars.createdGroups.find((group) =>
        group.calendars.some((cal) => cal._id === calendar._id)
      );
    }
  }, [calendar._id, userCalendars]);

  const onUnsubscribed = async (type) => {
    if (type === "removeFromGroup") {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        <>
          Calendar removed from your group. To add it back as a standalone
          calendar{" "}
          <Link
            as="span"
            sx={{ cursor: "pointer" }}
            onClick={async () => {
              await subscribe({ calendar });
              setSubscribed(true);
              onSubscribeAction?.(true, "calendar");
              setTimeout(() => {
                showSnackBar(
                  setSnackbarOpen,
                  setSnackbarMessage,
                  setSnackbarSeverity,
                  "Calendar added as standalone successfully.",
                  "success"
                );
              }, 1000);
            }}
          >
            click here
          </Link>{" "}
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              fontWeight: "300",
              fontSize: "1rem",
              verticalAlign: "text-bottom",
            }}
          >
            north_east
          </Icon>
        </>,
        "success"
      );
    } else {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "Calendar removed successfully.",
        "success"
      );
    }
    setSubscribed(false);
    onSubscribeAction?.(false);
    refetchAuth();
    refetchUserCalendars();
  };

  return (
    <>
      {!isEventCard &&
        (subscribed ? (
          <Box display="flex" flexDirection="column">
            <Stack direction="row" spacing={2}>
              {inGroup && calendarGroup ? (
                <Button
                  data-testid="button-already-added"
                  variant="outlined"
                  size="shrinked"
                  sx={{ minWidth: "172px", maxWidth: "172px" }}
                  onClick={() =>
                    navigate(`/${calendarGroup.handle}/${calendarGroup._id}`)
                  }
                >
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="medium"
                    sx={{
                      fontWeight: "300",
                      color: `${theme.palette.icon.primary}`,
                      mr: 0.75,
                    }}
                  >
                    folder
                  </Icon>
                  <Box
                    as="span"
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {calendarGroup.name}
                  </Box>
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="medium"
                    sx={{
                      fontWeight: "300",
                      color: `${theme.palette.icon.primary}`,
                      ml: 1,
                    }}
                  >
                    north_east
                  </Icon>
                </Button>
              ) : (
                <Button
                  data-testid="button-channel-action"
                  variant="outlined"
                  size="shrinked"
                  startIcon={
                    isChannelBlocked ? <RefreshOutlined /> : <DevicesOutlined />
                  }
                  sx={{ minWidth: "172px" }}
                  onClick={handleChannelAction}
                >
                  {channelActionLabel}
                </Button>
              )}
              <Tooltip title="Copy Link">
                <IconButton onClick={copyToClipboard} size="small">
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="medium"
                    sx={{
                      color: `${theme.palette.icon.primary}`,
                      verticalAlign: "middle",
                    }}
                  >
                    link
                  </Icon>
                </IconButton>
              </Tooltip>
              <ShareDialog
                calendar={calendar}
                handle={calendar.handle}
                as="IconButton"
              />
              <Tooltip title="Unsubscribe">
                <IconButton
                  onClick={() => {
                    handleUnsubscribe({
                      user,
                      calendar,
                      unsubscribeGroup,
                      refetchUserCalendars,
                      createdGroups: userCalendars?.createdGroups ?? [],
                      removeFromGroup,
                      unsubscribeCalendar,
                      onUnsubscribed,
                      showSnackBar,
                      setSnackbarOpen,
                      setSnackbarMessage,
                      setSnackbarSeverity,
                      setSubscribed,
                    });
                  }}
                  size="small"
                  data-testid="button-remove-from-calendar"
                >
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="medium"
                    sx={{
                      color: `${theme.palette.icon.primary}`,
                      verticalAlign: "middle",
                    }}
                  >
                    delete
                  </Icon>
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mt: 1.5 }}
            >
              Not seeing events in your calendar?{" "}
              {!hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key) ? (
                <>
                  {isChannelBlocked && (
                    <>
                      <Link sx={{ cursor: "pointer" }} onClick={handleChannelAction}>
                        {channelActionLabel}
                      </Link>{" "}
                      or{" "}
                    </>
                  )}
                  <Link
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://stanza-1.gitbook.io/stanza-help-center/for-subscribers/editor",
                        "_blank"
                      )
                    }
                  >
                    Troubleshoot
                  </Link>
                </>
              ) : (
                <Link
                  sx={{
                    cursor: !isLoadingForceRefreshGoogleCalendar
                      ? "pointer"
                      : "default",
                    position: "relative",
                  }}
                  onClick={() =>
                    !isLoadingForceRefreshGoogleCalendar &&
                    handleForceRefreshGoogleCalendar(
                      inGroup ? calendarGroup : calendar
                    )
                  }
                >
                  Force Refresh
                  {isLoadingForceRefreshGoogleCalendar && (
                    <IconButton
                      size="small"
                      disabled={true}
                      sx={{
                        position: "absolute",
                        ml: 0.4,
                        mt: -0.2,
                      }}
                    >
                      <CircularProgress size={14} color="inherit" />
                    </IconButton>
                  )}
                </Link>
              )}
            </Typography>
          </Box>
        ) : (
          <Stack direction="row" spacing={2}>
            <LoadingButton
              data-testid="add-to-calendar-button"
              data-timeline-add-to-calendar-button
              id="add-to-calendar-button"
              size="shrinked"
              variant="contained"
              loading={isLoadingOpenAddToCalendar}
              onClick={handleOpenAddToCalendar}
            >
              Add to Calendar
            </LoadingButton>
            <ShareDialog
              calendar={calendar}
              handle={calendar.handle}
              as="IconButton"
            />
          </Stack>
        ))}

      {isEventCard &&
        (subscribed ? (
          <Stack direction="row" spacing={0}>
            <Button
              data-testid="button-already-added"
              variant="text"
              size="small"
              sx={{ fontSize: "10px !important", letterSpacing: "-0.6px" }}
              disabled
            >
              {inGroup && calendarGroup ? (
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ cursor: "pointer" }}
                >
                  <Icon
                    baseClassName="material-symbols-outlined"
                    sx={{ fontWeight: "300", fontSize: "1rem", mr: 0.5 }}
                  >
                    folder
                  </Icon>{" "}
                  Added
                </Box>
              ) : (
                "Added"
              )}
            </Button>
            <Tooltip title="Copy Link">
              <IconButton onClick={copyToClipboard} size="small">
                <Icon
                  baseClassName="material-symbols-outlined"
                  fontSize="small"
                  sx={{
                    color: `${theme.palette.icon.primary}`,
                    verticalAlign: "middle",
                  }}
                >
                  link
                </Icon>
              </IconButton>
            </Tooltip>
            {!hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key) ? (
              <Tooltip title={channelActionLabel}>
                <IconButton onClick={handleChannelAction} size="small">
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="small"
                    sx={{
                      color: `${theme.palette.icon.primary}`,
                      verticalAlign: "middle",
                    }}
                  >
                    {isChannelBlocked ? "event_repeat" : "devices"}
                  </Icon>
                </IconButton>
              </Tooltip>
            ) : !isLoadingForceRefreshGoogleCalendar ? (
              <Tooltip title="Force Refresh">
                <IconButton
                  onClick={() =>
                    handleForceRefreshGoogleCalendar(
                      inGroup ? calendarGroup : calendar
                    )
                  }
                  size="small"
                  disabled={isLoadingForceRefreshGoogleCalendar}
                >
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="small"
                    sx={{
                      color: `${theme.palette.icon.primary}`,
                      verticalAlign: "middle",
                    }}
                  >
                    refresh
                  </Icon>
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton size="small" disabled={true}>
                <CircularProgress size={18} color="inherit" />
              </IconButton>
            )}
            <Tooltip title="Unsubscribe">
              <IconButton
                onClick={() => {
                  handleUnsubscribe({
                    user,
                    calendar,
                    unsubscribeGroup,
                    refetchUserCalendars,
                    createdGroups: userCalendars?.createdGroups ?? [],
                    removeFromGroup,
                    unsubscribeCalendar,
                    onUnsubscribed,
                    showSnackBar,
                    setSnackbarOpen,
                    setSnackbarMessage,
                    setSnackbarSeverity,
                    setSubscribed,
                  });
                }}
                size="small"
                data-testid="button-remove-from-calendar"
                sx={{ ml: "4px" }}
              >
                <Icon
                  baseClassName="material-symbols-outlined"
                  fontSize="small"
                  sx={{
                    color: `${theme.palette.icon.primary}`,
                    verticalAlign: "middle",
                  }}
                >
                  delete
                </Icon>
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <LoadingButton
            data-testid="add-to-calendar-button"
            id="add-to-calendar-button"
            size="small"
            variant="contained"
            loading={isLoadingOpenAddToCalendar}
            onClick={handleOpenAddToCalendar}
          >
            Add to Calendar
          </LoadingButton>
        ))}

      <NewGroupDialog
        open={openNewGroup}
        handleClose={() => setOpenNewGroup(false)}
        handleCreateGroup={handleCreateGroup}
        user={user}
        calendarId={targetCalendar._id}
        handleMessage={handleMessage}
      />
      <AddToGroupDialog
        createdGroups={createdGroups}
        open={openAddToGroup}
        handleClose={() => setOpenAddToGroup(false)}
        handleCreate={() => {
          setOpenAddToGroup(false);
          setOpenNewGroup(true);
        }}
        handleAdd={handleAddToGroup}
        busyGroupId={busyGroupId}
      />

      <SignInDialog
        open={openSignInDialog}
        handleClose={() => setOpenSignInDialog(false)}
        calendar={calendar}
      />

      {user && openAddToCalendarOrGroupDialog && (
        <AddToCalendarDialog
          open={openAddToCalendarOrGroupDialog}
          handleClose={() => setOpenAddToCalendarOrGroupDialog(false)}
          handleAddToCalendar={handleSubscribe}
          handleAddToGroup={handleGroupDialog}
          calendarId={calendar._id}
          handle={calendar.handle}
          initialCalendar={calendar}
          isEventCard={isEventCard}
        />
      )}

      <OpenCalendarDialog
        open={openCalendarDialog}
        handleClose={() => setOpenCalendarDialog(false)}
        calendar={calendar}
      />

      <FloatingButton
        open={openTimelineDialog}
        loading={isLoadingOpenAddToCalendar}
        onClick={handleOpenAddToCalendar}
        id="add-to-calendar-floating-button"
        dataTestId="add-to-calendar-floating-button"
      >
        Add to Calendar
      </FloatingButton>
    </>
  );
}

export default memo(SubscribeButton);
